/**
 * API-обёртки для всех endpoints + типы ответов.
 *
 * Используются через useQuery({queryKey, queryFn}) во всех страницах.
 * Заменяют прямые импорты из @/data/mock.
 */
import { api } from "./client";

/* ── /api/dashboard/overview ─────────────────────────────────── */
export interface OverviewResponse {
  today: { weekday: string; date_human: string; iso: string };
  period: { date_from: string; date_to: string };
  /** Список subtask'ов которые упали (active_deals/managers/leads_period/...).
   *  Непустой → фронт показывает alert «AmoCRM временно недоступен». */
  degraded?: string[];
  kpi: {
    active_deals: number;
    overdue_total: number;
    overdue_critical: number;
    today_leads: number;
    period_leads?: number;
  };
  month_revenue: number;
  period_revenue: number;
  today_revenue: number;
  today_leads: number;
  funnel: {
    stage: string;
    total: number;
    overdue: number;
    overdue_pct?: number;
    mean_days?: number;
    max_days?: number;
    entered_period: number;
  }[];
  /** {стадия: сколько уник. лидов вошло в неё за период}. Включает терминальные
   *  стадии («Сделка проиграна»), которых нет в funnel[] — там только активные.
   *  ⚠️ Регионом не фильтруется (см. комментарий в services/overview.py). */
  stage_entries?: Record<string, number>;
  /** {этап цепочки: лидов, дошедших до него или дальше за период} — монотонная воронка. */
  stage_reach?: Record<string, number>;
  funnel_by_manager?: {
    manager_id: number;
    name: string;
    active_total: number;
    overdue_total: number;
    funnel: {
      stage: string;
      total: number;
      overdue: number;
      overdue_pct?: number;
      mean_days?: number;
      max_days?: number;
      entered_period: number;
    }[];
  }[];
  alerts?: {
    severity: "red" | "amber";
    title: string;
    client?: string;
    manager?: string;
    amo_url?: string;
  }[];
  top_managers?: {
    name: string;
    revenue: number;
    contracts: number;
    conv_pct: number;
  }[];
  contracts_period_list?: {
    id: number;
    name: string;
    price: number;
    region: string;
    responsible_user_id: number | null;
    responsible_name: string;
    signed_at: number | null;     // unix ts
    created_at: number | null;    // unix ts
    amo_url: string;
    stage_name: string;
    source: string | null;        // «Источник» из Amo (или utm_source как fallback)
    current_stage?: string | null;       // фактическая текущая стадия в Amo
    is_active_contract?: boolean;        // сейчас в «Договор заключён»/после-договорной/won
    is_deferred?: boolean;               // сейчас в «Договор отложенный»
    created_in_stage?: boolean;          // создана сразу в договорной стадии (без перехода)
  }[];
}
export const fetchOverview = (params?: { date_from?: string; date_to?: string; region?: string }): Promise<OverviewResponse> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to)   q.set("date_to", params.date_to);
  if (params?.region && params.region !== "all") q.set("region", params.region);
  const qs = q.toString();
  return api(`/api/dashboard/overview${qs ? "?" + qs : ""}`);
};

/* ── /api/dashboard/callcenter ───────────────────────────────── */
export interface CallcenterMetricsResponse {
  period: { from: string; to: string };
  transferred?: number;            // лиды переданы КЦ → sales
  kc_total_appeared?: number;      // лидов появилось в КЦ за период (через transitions)
  conversion_pct?: number | null;  // % переданных
  kc_new_leads?: number | null;    // новые лиды (created_at) в КЦ-пайплайнах
  kc_first_touch?: {
    total_leads: number;
    with_call: number;
    without_call: number;
    within_15min: number;
    within_40min: number;
    within_40min_pct: number | null;
    avg_minutes: number | null;
    p50_minutes: number | null;
    late_critical: number;
  } | null;
  error?: string;
}
export const fetchCallcenterMetrics = (params?: { date_from?: string; date_to?: string }): Promise<CallcenterMetricsResponse> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to)   q.set("date_to", params.date_to);
  const qs = q.toString();
  return api(`/api/dashboard/callcenter${qs ? "?" + qs : ""}`);
};


/* ── /api/dashboard/ai-managers-analysis ─────────────────────── */
export interface AiAnalysisResponse {
  period?: { from: string; to: string };
  region?: string;
  model?: string;
  cached?: boolean;
  tokens?: { input: number; output: number };
  summary?: string;
  top_performer?: { name: string; why: string };
  needs_attention?: { name: string; issue: string; action: string }[];
  team_patterns?: string[];
  recommendations?: string[];
  error?: string;
  _parse_error?: string;
}
export const fetchAiManagersAnalysis = (params?: { date_from?: string; date_to?: string; region?: string }): Promise<AiAnalysisResponse> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to)   q.set("date_to", params.date_to);
  if (params?.region && params.region !== "all") q.set("region", params.region);
  const qs = q.toString();
  return api(`/api/dashboard/ai-managers-analysis${qs ? "?" + qs : ""}`);
};

/* Сохранённые AI-отчёты — история */
export interface AiReportRow {
  id: number;
  period_from: string;
  period_to: string;
  region: string | null;
  created_at: string;
  created_by_name: string | null;
}
export interface AiReportFull extends AiReportRow {
  payload: AiAnalysisResponse;
}
export const fetchAiReports = (): Promise<AiReportRow[]> =>
  api(`/api/dashboard/ai-managers-reports`);
export const fetchAiReport = (id: number): Promise<AiReportFull> =>
  api(`/api/dashboard/ai-managers-reports/${id}`);


/* ── /api/dashboard/tasks-summary ────────────────────────────── */
export interface TasksSummaryResponse {
  period: { from: string; to: string };
  total: { today: number; completed: number; open: number; overdue: number; rescheduled: number };
  by_manager: {
    manager_id: number;
    name: string;
    today: number;
    completed: number;
    open: number;
    overdue: number;
    rescheduled: number;
  }[];
  error?: string;
}
export const fetchTasksSummary = (params?: { date_from?: string; date_to?: string; region?: string }): Promise<TasksSummaryResponse> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to)   q.set("date_to", params.date_to);
  if (params?.region && params.region !== "all") q.set("region", params.region);
  const qs = q.toString();
  return api(`/api/dashboard/tasks-summary${qs ? "?" + qs : ""}`);
};


/* ── /api/dashboard/first-touch ──────────────────────────────── */
export interface FirstTouchResponse {
  period: { from: string; to: string };
  total_leads: number;
  with_call: number;
  without_call: number;
  within_15min: number;
  within_40min: number;
  within_120min: number;
  late_critical: number;
  within_40min_pct: number | null;
  avg_minutes: number | null;
  p50_minutes: number | null;
  p90_minutes: number | null;
  by_manager: {
    manager_id: number;
    name: string;
    leads: number;
    with_call: number;
    without_call: number;
    within_40min: number;
    within_40min_pct: number | null;
    late_critical: number;
    avg_minutes: number | null;
    p50_minutes: number | null;
  }[];
  error?: string;
}
export const fetchFirstTouch = (params?: { date_from?: string; date_to?: string; region?: string }): Promise<FirstTouchResponse> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to)   q.set("date_to", params.date_to);
  if (params?.region && params.region !== "all") q.set("region", params.region);
  const qs = q.toString();
  return api(`/api/dashboard/first-touch${qs ? "?" + qs : ""}`);
};


/* ── /api/managers/ ──────────────────────────────────────────── */
export interface ManagersResponse {
  period: { from: string; to: string };
  total_amo_managers: number;
  managers: ManagerRow[];
}
export interface ManagerRow {
  name: string;
  manager_id: number;
  region: string;       // "Москва" | "Юг" | "СПб"
  calls: number;
  avg_score: number | null;
  good_calls: number;
  bad_calls: number;
  low_data: boolean;
  violations: number;
  unique_violations_deals: number;
  new_leads: number;
  qualified: number;
  visits: number;
  contracts: number;          // все ручные переходы в «Договор заключён» за период
  contracts_active?: number;  // из них сейчас в стадии договора (без откатов)
  revenue: number;
}
export const fetchManagers = (params?: { date_from?: string; date_to?: string; region?: string }): Promise<ManagersResponse> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to)   q.set("date_to", params.date_to);
  if (params?.region && params.region !== "all") q.set("region", params.region);
  const qs = q.toString();
  return api(`/api/managers/${qs ? "?" + qs : ""}`);
};

/* ── /api/calls/ + /summary ──────────────────────────────────── */
export interface CallRow {
  id: number;
  called_at: string;
  direction: "in" | "out";
  duration_sec: number;
  manager_id: number | null;
  manager_name: string | null;
  client_phone: string | null;
  client_name: string | null;
  lead_id: number | null;
  stage_before: string | null;
  stage_after: string | null;
  moved_forward: boolean;
  summary: string | null;
  score_total: number | null;
  score_script: number | null;
  score_needs: number | null;
  score_presentation: number | null;
  score_objections: number | null;
  score_next_step: number | null;
  score_tone: number | null;
  objection_raised: boolean | null;
  next_step_set: boolean | null;
  needs_review: boolean;
  client_sentiment: string | null;
  stage_alignment: string | null;
  audio_source?: string;  // mango | attached
  has_audio?: boolean;
  short_reason?: string | null;
  transcript?: string | null;
  transcript_by_roles?: string | null;
}
export interface CallsSummary {
  metrics: {
    total: number;
    meaningful: number;
    short_calls: number;
    avg_score: number;
    bad: number;
    good: number;
    with_next_step: number;
    moved: number;
    by_mango?: number;       // звонки через Mango Office (виджет/моб. клиент)
    by_attached?: number;    // прикреплённые записи (микрофон / личный телефон)
  };
  managers: {
    manager_id: number;
    manager_name: string;
    calls: number;
    avg_score: number | null;
    good: number;
    bad: number;
    next_step_count: number;
    moved: number;
    low_data: boolean;
  }[];
  period: { from: string; to: string };
}
function withRange(path: string, params?: { date_from?: string; date_to?: string; region?: string; manager_id?: number }): string {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to)   q.set("date_to", params.date_to);
  if (params?.region && params.region !== "all") q.set("region", params.region);
  if (params?.manager_id !== undefined) q.set("manager_id", String(params.manager_id));
  const qs = q.toString();
  return path + (qs ? (path.includes("?") ? "&" : "?") + qs : "");
}

type RangeParams = { date_from?: string; date_to?: string; region?: string; manager_id?: number };

export const fetchCalls        = (params?: RangeParams): Promise<CallRow[]>    => api(withRange("/api/calls/", params));
export const fetchCallsSummary = (params?: RangeParams): Promise<CallsSummary> => api(withRange("/api/calls/summary", params));

export interface DealsNoCallsRow {
  id: number;
  name: string;
  amo_url: string | null;
  stage: string | null;
  region: string | null;
  created_at: string | null;
  price: number | null;
}
export interface DealsNoCallsManager {
  manager_id: number;
  manager_name: string;
  deals_total: number;
  deals_no_calls: number;
  deals_list: DealsNoCallsRow[];
}
export interface DealsNoCallsResponse {
  managers: DealsNoCallsManager[];
  period: { from: string; to: string };
}

export const fetchDealsNoCalls = (params?: RangeParams): Promise<DealsNoCallsResponse> =>
  api(withRange("/api/calls/deals-no-calls", params));

export interface DealReviewTask {
  id: number;
  text: string;
  complete_till: number | null;
  is_completed: boolean;
  result_text: string;
}
export interface RedFlag {
  type: string;
  severity: "low" | "medium" | "high" | "critical";
  label: string;
  detail: string;
  weight: number;
}
export interface SuspiciousMatch {
  keyword_id: number;
  matched_text: string;
  snippet: string;
  weight: number;
  severity: string;
  description: string;
  source: "note" | "call_summary";
  source_id: number | null;
  created_at: number | null;
}
export interface AiVerdict {
  verdict?: string;
  recommended_action?: string;
  model?: string;
  tokens?: { input: number; output: number };
  cached?: boolean;
  error?: string;
  _parse_error?: string;
}
export interface DealReview {
  deal_id: number;
  current_stage: string | null;
  stage_entered_ts: number | null;
  days_on_stage: number | null;
  last_contact_ts: number | null;
  days_since_contact: number | null;
  risk_score: number;
  risk_level: "ok" | "amber" | "red";
  red_flags: RedFlag[];
  suspicious_matches: SuspiciousMatch[];
  calls_count: number;
  last_call: {
    called_at: string;
    ai_score: number | null;
    ai_summary: string;
    duration_sec: number | null;
    direction: string | null;
  } | null;
  notes_total: number;
  manual_notes_count: number;
  ai_notes_count: number;
  last_manual_note: { created_at: number; text: string } | null;
  last_ai_note: { created_at: number; text: string } | null;
  tasks: DealReviewTask[];
  tasks_open: number;
  tasks_completed: number;
  next_task: {
    id: number;
    text: string;
    complete_till: number;
    days_to_next_task: number | null;
    days_gap_after_contact: number | null;
  } | null;
  ai_verdict?: AiVerdict;
}

export const fetchDealReview = (dealId: number, withAi: boolean = false): Promise<DealReview> =>
  api(`/api/calls/deals-no-calls/deal/${dealId}/review?with_ai=${withAi ? 1 : 0}`);

/* ── /api/calls/lost-deals-audit ────────────────────────────── */
export interface LostDealRow {
  id: number;
  name: string;
  amo_url: string | null;
  stage: string | null;
  region: string | null;
  closed_at: number | null;
  price: number | null;
  calls_count: number;
  risk_score: number;
  risk_level: "ok" | "amber" | "red";
  red_flags: RedFlag[];
}
export interface LostManagerAudit {
  manager_id: number;
  manager_name: string;
  lost_total: number;
  lost_with_red_flags: number;
  deals: LostDealRow[];
}
export interface LostAuditResponse {
  managers: LostManagerAudit[];
  period: { from: string; to: string };
  total_lost: number;
}
export const fetchLostDealsAudit = (params?: { days?: number; region?: string | null }): Promise<LostAuditResponse> => {
  const qs = new URLSearchParams();
  if (params?.days) qs.set("days", String(params.days));
  if (params?.region) qs.set("region", params.region);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return api(`/api/calls/lost-deals-audit${suffix}`);
};

/* ── /api/lead-theft/keywords (anti-fraud словарь) ────────────── */
export interface SuspiciousKeywordRow {
  id: number;
  pattern: string;
  weight: number;
  severity: "low" | "medium" | "high" | "critical";
  description: string | null;
  is_active: boolean;
  created_at: string | null;
  updated_at: string | null;
}
export interface KeywordCreatePayload {
  pattern: string;
  weight: number;
  severity: "low" | "medium" | "high" | "critical";
  description?: string;
  is_active?: boolean;
}
export type KeywordUpdatePayload = Partial<KeywordCreatePayload>;

export const fetchKeywords = (): Promise<SuspiciousKeywordRow[]> =>
  api("/api/lead-theft/keywords");

export const createKeyword = (payload: KeywordCreatePayload): Promise<SuspiciousKeywordRow> =>
  api("/api/lead-theft/keywords", { method: "POST", body: JSON.stringify(payload) });

export const patchKeyword = (id: number, payload: KeywordUpdatePayload): Promise<SuspiciousKeywordRow> =>
  api(`/api/lead-theft/keywords/${id}`, { method: "PATCH", body: JSON.stringify(payload) });

export const deleteKeyword = (id: number): Promise<void> =>
  api(`/api/lead-theft/keywords/${id}`, { method: "DELETE" });

/* ── /api/violations/ ────────────────────────────────────────── */
export interface ViolationsResponse {
  period: { from: string; to: string };
  totals: { snapshots: number; total_violations: number; chronic_deals: number };
  daily: { snapshot_date: string; deals: number; total_violations: number }[];
  by_manager?: {
    manager_name: string;
    unique_deals: number;
    total_violations: number;
    deal_days: number;
    avg_per_snapshot: number;
  }[];
  chronic?: {
    amo_id: number;
    manager_name: string;
    client_name: string;
    stage: string;
    region: string;
    times_seen: number;
    max_violations: number;
    max_days_on_stage: number;
    max_days_total: number;
    first_seen: string;
    last_seen: string;
  }[];
  available_dates?: string[];
}
export const fetchViolations = (params?: RangeParams): Promise<ViolationsResponse> => api(withRange("/api/violations/", params));

export interface ViolationItem {
  amo_id: number;
  client_name: string;
  manager_name: string | null;
  region: string | null;
  stage: string | null;
  problem: string | null;
  violation_count: number;
  days_on_stage: number;
  days_total: number;
  resolved?: boolean;
  resolved_at?: string | null;
  resolved_by_name?: string | null;
}
export interface CurrentViolationsResponse {
  snapshot_date: string | null;
  items: ViolationItem[];
  by_manager: { manager_name: string; unique_deals: number; total_violations: number }[];
  by_type: { no_tasks: number; overdue_task: number; over_norm: number };
  totals: { deals: number; violations: number; resolved?: number };
  can_resolve?: boolean;
}
export const fetchCurrentViolations = (region?: string | null): Promise<CurrentViolationsResponse> => {
  const q = region ? `?region=${encodeURIComponent(region)}` : "";
  return api(`/api/violations/current${q}`);
};
export const resolveViolation = (snapshot_date: string, amo_id: number): Promise<{ ok: boolean }> =>
  api(`/api/violations/resolve`, { method: "POST", body: JSON.stringify({ snapshot_date, amo_id }) });
export const unresolveViolation = (snapshot_date: string, amo_id: number): Promise<{ ok: boolean }> =>
  api(`/api/violations/unresolve`, { method: "POST", body: JSON.stringify({ snapshot_date, amo_id }) });

/* ── /api/reports/leads — отчёт по тёплым/квалам/выездам ────────────── */
export interface ReportNote {
  date_str: string;
  ago: string;
  text: string;
}
export interface ReportCall {
  date_str: string;
  ago: string;
  summary: string;
}
export interface ReportNextTask {
  deadline_str: string;
  when: string;
  text: string;
}
export interface ReportLocalNote {
  id: number;
  text: string;
  author_name: string;
  created_at: string;
}
export interface ReportDeal {
  amo_id: number;
  name: string;
  url: string;
  manager: string;
  stage_name: string | null;
  region: string | null;
  // Локальные примечания (показываются перед сделкой в UI)
  notes?: ReportLocalNote[];
  // Богатые поля для warm/qual:
  days_on_stage_str?: string | null;
  regulation_status?: "ok" | "alert" | "breach";
  next_task?: ReportNextTask | null;
  alert_kind?: "alert" | "breach" | null;
  alert_text?: string | null;
  last_note?: ReportNote | null;
  last_call?: ReportCall | null;
  has_call?: boolean;
  // Поля только для visits:
  visit_at_str?: string | null;
}
export interface ReportGroupStats {
  no_calls: number;
  breach: number;
  alert: number;
  ok: number;
}
export interface ReportGroup {
  manager: string;
  tag: string;
  count: number;
  stats?: ReportGroupStats;
  deals: ReportDeal[];
}
export interface ReportLeadsResponse {
  category: "warm" | "qual" | "visits";
  stages: string[];
  total: number;
  groups: ReportGroup[];
  snapshot_at?: string;
  from_cache?: boolean;
}
export const refreshReportCache = (): Promise<{ ok: boolean; snapshot_at: string; summary: Record<string, number> }> =>
  api(`/api/reports/refresh`, { method: "POST" });
export type ReportCategory = "warm" | "qual" | "kp_sent" | "visits" | "deal_sent" | "tech" | "price";
/** Воронка «Контроля сделок»: беседки (Отдел продаж / Москва) или бани
 *  (Бани / Крепкий пар). Снимки у них разные, поэтому параметр уходит в API. */
export type ReportFunnel = string;   // ключ воронки CRM компании (04.10.2026; у КБ — besedki / bani)
export const fetchReportLeads = (category: ReportCategory, funnel: ReportFunnel = "besedki"): Promise<ReportLeadsResponse> =>
  api(`/api/reports/leads?category=${category}&funnel=${funnel}`);
/** Вкладки-воронки «Контроля сделок» и «Фокусных» (04.10.2026): у КБ — Беседки и Бани, у компании brixcrm — её. */
export interface ReportFunnelInfo { key: string; name: string }
export const fetchReportFunnels = (): Promise<ReportFunnelInfo[]> => api("/api/reports/funnels");
export interface AddNoteResult {
  ok: boolean;
  amo_note_id: number | null;
  production_note_added: boolean;
  note: ReportLocalNote & { amo_id: number };
}
export const addDealReportNote = (
  amo_id: number,
  text: string,
  propagate_to_production = false,
  category?: ReportCategory,
): Promise<AddNoteResult> =>
  api(`/api/reports/note`, {
    method: "POST",
    body: JSON.stringify({ amo_id, text, propagate_to_production, category }),
  });

/* ── /api/focal-deals/ — фокусные сделки ─────────────────────── */
export interface FocalNote {
  id: number;
  text: string;
  author_name: string | null;
  created_at: string;
}
export interface FocalDeal {
  amo_lead_id: number;
  marked_by_user_id: string;
  marked_by_name: string | null;
  marked_at: string;
  source_category: string | null;
  client_name: string | null;
  client_amo_url: string | null;
  manager_name: string | null;
  engineer_name: string | null;
  region: string | null;
  visit_at: string | null;
  sum_contract: number | null;
  amo_stage: string | null;
  note: string | null;
  // Поля для текстового отчёта (см. PatchFocalIn в backend):
  status: string | null;              // 'potential' | 'thinking' | 'rejected' | null
  next_contact_date: string | null;   // YYYY-MM-DD
  next_contact_source: string | null; // 'manual' | 'amo_task'
  info_verified: boolean;
  last_amo_note: string | null;       // последний Amo-note из snapshot'а (read-only)
  amo_price: number | null;           // цена сделки из Amo (read-only из snapshot'а)
  // Воронка Amo, в которой сделка лежит сейчас. Беседки и Бани — разные
  // бизнесы, в реестре они разведены по вкладкам.
  pipeline_id: number | null;
  pipeline_name: string | null;
  funnel: string;                 // besedki | bani | other у КБ; у компании brixcrm — ключ её воронки
  funnel_label: string;
  notes?: FocalNote[];
}
export interface PatchFocalPayload {
  status?: string | null;
  next_contact_date?: string | null;
  next_contact_source?: string | null;
  info_verified?: boolean;
  sum_contract?: number | null;
  note?: string | null;
}
export const patchFocalDeal = (amoId: number, payload: PatchFocalPayload): Promise<FocalDeal> =>
  api(`/api/focal-deals/${amoId}`, { method: "PATCH", body: JSON.stringify(payload) });

export interface FocalDealsList {
  items: FocalDeal[];
  total: number;
}
export interface MarkFocalPayload {
  source_category?: string | null;
  client_name?: string | null;
  client_amo_url?: string | null;
  manager_name?: string | null;
  engineer_name?: string | null;
  region?: string | null;
  visit_at?: string | null;
  sum_contract?: number | null;
  amo_stage?: string | null;
  note?: string | null;
}
export const fetchFocalDeals = (): Promise<FocalDealsList> =>
  api(`/api/focal-deals`);
export const fetchFocalDealIds = (): Promise<{ ids: number[] }> =>
  api(`/api/focal-deals/ids`);
export const markFocalDeal = (amoId: number, payload: MarkFocalPayload): Promise<FocalDeal> =>
  api(`/api/focal-deals/${amoId}`, { method: "POST", body: JSON.stringify(payload) });
export const unmarkFocalDeal = (amoId: number): Promise<void> =>
  api(`/api/focal-deals/${amoId}`, { method: "DELETE" });
export interface FocalSyncResult {
  total: number;
  synced: number;
  gone: number;
  failed: number;
  by_funnel: Record<string, number>;
}
/** Перечитать воронку и цену всех фокусных сделок из AmoCRM. */
export const syncFocalDealsAmo = (): Promise<FocalSyncResult> =>
  api(`/api/focal-deals/sync-amo`, { method: "POST" });

/* ── /api/voice-notes/ — голосовые примечания ─────────────────── */
export interface VoiceNoteSendResult {
  ok: boolean;
  amo_note_id: number | null;
  production_note_added: boolean;
  audio_url: string;
  duration_sec: number;
  size_bytes: number;
  transcript: string;
  note: {
    id: number;
    amo_id: number;
    text: string;
    author_name: string;
    created_at: string;
  };
}
export const sendVoiceNote = async (
  amoId: number,
  file: Blob,
  durationSec: number,
  opts?: { category?: string; propagateToProduction?: boolean; filename?: string },
): Promise<VoiceNoteSendResult> => {
  const fd = new FormData();
  fd.append("file", file, opts?.filename ?? `voice-${Date.now()}.webm`);
  fd.append("amo_id", String(amoId));
  fd.append("duration_sec", String(durationSec));
  if (opts?.category) fd.append("category", opts.category);
  if (opts?.propagateToProduction) fd.append("propagate_to_production", "true");
  return api(`/api/voice-notes/send`, { method: "POST", body: fd });
};

/* ── /api/reports/ ───────────────────────────────────────────── */
export interface ReportRow {
  id: number;
  report_date: string;
  manager_name: string;
  report_type: string;
  raw_text: string;
  created_at: string;
}
export const fetchReports = (params?: RangeParams): Promise<ReportRow[]> => api(withRange("/api/reports/", params));

/* ── /api/settings/ ──────────────────────────────────────────── */
export interface SettingsResponse {
  revenue_plan: number;
  thresholds: {
    voicemail_per_day: number;
    new_lead_max_minutes: number;
    no_activity_days: number;
    min_call_sec: number;
  };
  targets: {
    kpi_40min_pct: number;
    conv_kval_pct: number;
    conv_visit_pct: number;
    conv_contract_pct: number;
  };
  telegram_manager_map: Record<string, string>;
  manual_kts_leads: number[];
  excluded_kts_leads: number[];
  schedule: {
    daily_report_time: string;
    weekly_report_time: string;
    manager_deadline: string;
  };
  users: Record<string, { name: string; role: string; password_hash: string }>;
}
export const fetchSettings  = (): Promise<SettingsResponse> => api("/api/settings/");
export const updateSettings = (patch: Partial<SettingsResponse>): Promise<SettingsResponse> =>
  api("/api/settings/", { method: "PUT", body: JSON.stringify(patch) });

/* ── /api/settings/users CRUD (legacy settings.json — оставлено) ── */
export interface UserCreatePayload { login: string; name: string; role: "head_op" | "op_manager"; password: string; }
export interface UserUpdatePayload { name?: string; role?: "admin" | "head_op" | "op_manager"; password?: string; }

export const createDashboardUser = (payload: UserCreatePayload): Promise<SettingsResponse> =>
  api("/api/settings/users", { method: "POST", body: JSON.stringify(payload) });

export const editDashboardUser = (login: string, payload: UserUpdatePayload): Promise<SettingsResponse> =>
  api(`/api/settings/users/${encodeURIComponent(login)}`, { method: "PUT", body: JSON.stringify(payload) });

export const deleteDashboardUser = (login: string): Promise<SettingsResponse> =>
  api(`/api/settings/users/${encodeURIComponent(login)}`, { method: "DELETE" });


/* ── /api/chat — топики и сообщения команды ──────────────────── */
export interface ChatTopic {
  id: number;
  slug: string;
  name: string;
  icon: string | null;
  sort_order: number;
  last_message: { id: number; preview: string; text?: string; author: string; ts: string; from_me?: boolean; read_by_others?: boolean } | null;
  unread_count: number;
  unread_mentions?: number;
  last_read_id?: number;
  manual_unread?: boolean;
  kind?: "public" | "private_group" | "dm" | "drive" | "tasks" | "group" | "docs" | "reports" | "focal" | "favorites" | "visits_schedule" | "visits_funnel";
  created_by_user_id?: string | null;
  parent_topic_id?: number | null;
  is_pinned?: boolean;        // запинено юзером (per-user)
  pinned_at?: string | null;        // когда закреплён (для стабильного порядка)
  pin_order?: number | null;        // ручной порядок закреплённых (per-user drag)
  dm_partner?: { user_id: string; display_name: string | null; avatar_filename: string | null; last_seen_at?: string | null; departments?: string[]; position?: string | null; role?: string | null; is_superuser?: boolean } | null;
  department?: string | null;
  avatar_filename?: string | null;
}
export interface ChatTopicMember {
  user_id: string;
  display_name: string | null;
  avatar_filename: string | null;
  added_at: string;
  added_by_user_id: string | null;
  role?: "member" | "admin";
  user_role?: string | null;
  last_seen_at?: string | null;
}
export interface ChatAttachment {
  id: number;
  filename: string;
  mime: string | null;
  size_bytes: number;
  duration_sec?: number | null;        // для голосовых
  transcription?: string | null;        // появится после Whisper
  waveform?: number[] | null;           // 38 пиков амплитуды 0..1
  width?: number | null;                // точный pixel-width картинки/видео
  height?: number | null;               // точный pixel-height
  // Stripped thumbnail (TG-style): base64 JPEG ~32px, ~60-150 байт. Используем
  // как placeholder через background-image data: URL + filter:blur(8px),
  // пока грузится /preview (600px). Для видео — приходит async после
  // _generate_video_thumbnail_bg (см. backend).
  thumb_b64?: string | null;
}
export interface ChatReplyPreview {
  id: number;
  author_name: string;
  preview: string;
  deleted: boolean;
  /** Если ответ — это цитата выделенного фрагмента, тут хранится фрагмент.
   *  Рендерится как блок-цитата над основным текстом ответа. */
  quote?: string | null;
}
export const forwardChatMessages = (
  targetTopicId: number,
  sourceMessageIds: number[],
  sourceAttachmentIds: number[] = [],
): Promise<ChatMessage[]> => {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  return api(`${base}/chat/topics/${targetTopicId}/forward`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      source_message_ids: sourceMessageIds,
      source_attachment_ids: sourceAttachmentIds,
    }),
  });
};
export interface UserMini {
  user_id: string;
  display_name: string;
  avatar_filename: string | null;
}
export interface ChatReaction {
  emoji: string;
  count: number;
  mine: boolean;
  users?: UserMini[];
}
export interface ChatMessage {
  id: number;
  topic_id: number;
  author_user_id: string | null;
  author_name: string;
  author_avatar_filename?: string | null;
  author_last_seen_at?: string | null;
  text: string;
  created_at: string;  // iso (naive UTC)
  edited_at?: string | null;
  deleted_at?: string | null;
  pinned_at?: string | null;
  reply_to?: ChatReplyPreview | null;
  attachments?: ChatAttachment[];
  reactions?: ChatReaction[];
  read_by?: UserMini[];
  forwarded_from_message_id?: number | null;
  forwarded_from_author_name?: string | null;
  /** Метаданные для UI: inline-кнопки бота {buttons: [{label, command}]}. */
  meta?: ChatMessageMeta | null;
}

export interface ChatMessageButton {
  label: string;
  command: string;
}

/** Server-side link preview (генерится backend'ом async после send). */
export interface ChatLinkPreview {
  url: string;
  title?: string;
  description?: string;
  image_url?: string;
  site_name?: string;
}

export interface ChatMessageMeta {
  /** Идентификатор клиентской попытки отправки для защиты от дублей. */
  client_request_id?: string;
  buttons?: ChatMessageButton[];
  link_preview?: ChatLinkPreview;
  /** Результаты видео-задач, ключ = attachment_id (строкой). Живут в meta
   *  сообщения с видео: транскрипт/постмит рисуются прям под видео в том же
   *  бабле, отдельных сообщений бота по ним нет. Прилетают async через
   *  message_edit (как link_preview). Текст транскрипта — в
   *  attachment.transcription, текст постмита — здесь в postmit.text. */
  video_jobs?: Record<string, ChatVideoJobs>;
}
export interface ChatVideoJobs {
  transcript?: ChatVideoJob;
  postmit?: ChatVideoJob;
}
export interface ChatVideoJob {
  /** Имя сохранённого .docx */
  docx?: string;
  /** Название Диска отдела, куда сохранён docx */
  drive?: string;
  /** Только для постмита: готовый markdown */
  text?: string;
  /** Топик Диска, куда лёг docx — по нему строится кнопка перехода */
  topic_id?: number;
  /** Папка внутри Диска (может отсутствовать — тогда корень) */
  folder_id?: number | null;
  /** id вложения самого docx */
  att_id?: number;
}
export interface SendChatMessagePayload {
  text?: string;
  reply_to_id?: number | null;
  /** Выделенный фрагмент оригинала (для quote-reply). Должен идти вместе с
   *  reply_to_id, без него игнорируется backend'ом. */
  reply_quote?: string | null;
  attachment_ids?: number[];
  /** Callback-pattern для бота: text = label кнопки (виден юзеру),
   *  meta.bot_command = команда (скрытая, обрабатывается ботом). */
  meta?: Record<string, unknown>;
}
export const fetchChatTopics = (): Promise<ChatTopic[]> => api("/api/chat/topics");
/** Lazy-получить персональную «Избранное» юзера (создаётся при первом вызове). */
export const ensureFavoritesTopic = (): Promise<ChatTopic> => api("/api/chat/favorites/topic");
/** Компактная карта прочтений топика (reads_map-формат): по записи на читателя. */
interface ReadEntry { user_id: string; display_name: string; avatar_filename: string | null; last_read_id: number; }
type MessagesResp = ChatMessage[] | { messages: ChatMessage[]; reads: ReadEntry[] };
/** Нормализуем ответ /messages. Новый формат {messages, reads}: разворачиваем
 *  read_by клиентски (сообщение прочитано юзером, если его last_read_id >= id
 *  и он не автор) — тот же результат, что раньше слал сервер, но карта на
 *  проводе в разы меньше (O(юзеров) вместо O(сообщений×юзеров)). Старый формат
 *  (массив) возвращаем как есть — так откат бэка безопасен. */
function normalizeMessages(resp: MessagesResp): ChatMessage[] {
  if (Array.isArray(resp)) return resp;
  const { messages, reads } = resp;
  for (const m of messages) {
    m.read_by = reads
      .filter((r) => r.last_read_id >= m.id && r.user_id !== m.author_user_id)
      .map((r) => ({ user_id: r.user_id, display_name: r.display_name, avatar_filename: r.avatar_filename }));
  }
  return messages;
}
export const fetchChatMessages = async (topicId: number, afterId = 0, limit = 200, hideDeleted = false): Promise<ChatMessage[]> => {
  const q = new URLSearchParams({ reads_map: "1" });
  if (afterId) q.set("after_id", String(afterId));
  if (limit !== 200) q.set("limit", String(limit));
  if (hideDeleted) q.set("hide_deleted", "1");
  return normalizeMessages(await api<MessagesResp>(`/api/chat/topics/${topicId}/messages?${q.toString()}`));
};
/** Sliced history: окно вокруг конкретного id. Использовать при jump-to-message
 *  если target не загружен в текущей ленте (например, reply на сообщение
 *  десятилетней давности). Возвращает ~limit штук: половину до, половину после. */
export const fetchChatMessagesAround = async (topicId: number, aroundId: number, limit = 80, hideDeleted = false): Promise<ChatMessage[]> => {
  const q = new URLSearchParams({ around_id: String(aroundId), limit: String(limit), reads_map: "1" });
  if (hideDeleted) q.set("hide_deleted", "1");
  return normalizeMessages(await api<MessagesResp>(`/api/chat/topics/${topicId}/messages?${q.toString()}`));
};
/** Sliced history: страница ДО указанного id (для scroll-up подгрузки). */
export const fetchChatMessagesBefore = async (topicId: number, beforeId: number, limit = 80, hideDeleted = false): Promise<ChatMessage[]> => {
  const q = new URLSearchParams({ before_id: String(beforeId), limit: String(limit), reads_map: "1" });
  if (hideDeleted) q.set("hide_deleted", "1");
  return normalizeMessages(await api<MessagesResp>(`/api/chat/topics/${topicId}/messages?${q.toString()}`));
};
/** Drafts (TG-style композер-черновики, sync через PUT/DELETE при idle 1.5с). */
export interface ChatDraft {
  topic_id: number;
  text: string;
  reply_to_id: number | null;
  updated_at: string;
}
export const fetchChatDrafts = (): Promise<ChatDraft[]> => api("/api/chat/drafts");
export const saveChatDraft = (topicId: number, text: string, replyToId: number | null): Promise<ChatDraft> =>
  api(`/api/chat/topics/${topicId}/draft`, {
    method: "PUT",
    body: JSON.stringify({ text, reply_to_id: replyToId }),
  });
export const deleteChatDraft = (topicId: number): Promise<void> =>
  api(`/api/chat/topics/${topicId}/draft`, { method: "DELETE" });
/** signal — AbortSignal отправки (ChatPage: таймаут 20 с + обрыв зависшего
 *  запроса на возврате в PWA); без него зависший POST держал композер навсегда. */
export const sendChatMessage = (topicId: number, payload: SendChatMessagePayload, signal?: AbortSignal): Promise<ChatMessage> =>
  api(`/api/chat/topics/${topicId}/messages`, {
    method: "POST",
    body: JSON.stringify({
      text: payload.text ?? "",
      reply_to_id: payload.reply_to_id ?? null,
      reply_quote: payload.reply_quote ?? null,
      attachment_ids: payload.attachment_ids ?? [],
      meta: payload.meta ?? null,
    }),
    ...(signal ? { signal } : {}),
  });
export const editChatMessage = (messageId: number, text: string): Promise<ChatMessage> =>
  api(`/api/chat/messages/${messageId}`, {
    method: "PATCH", body: JSON.stringify({ text }),
  });
export const deleteChatMessage = (messageId: number): Promise<void> =>
  api(`/api/chat/messages/${messageId}`, { method: "DELETE" });
export const restoreChatMessage = (messageId: number): Promise<void> =>
  api(`/api/chat/messages/${messageId}/restore`, { method: "POST" });
export const markChatRead = (topicId: number, upToId: number): Promise<{ status: string }> =>
  api(`/api/chat/topics/${topicId}/read?up_to_id=${upToId}`, { method: "POST" });
export const uploadChatFile = (file: File): Promise<ChatAttachment> => {
  const fd = new FormData();
  fd.append("file", file);
  return api(`/api/chat/upload`, { method: "POST", body: fd });
};
/** Создать «осиротевший» дубль chat_attachment (uploader = текущий юзер,
 *  message_id = NULL). Используется при прикреплении файла с Диска. */
export const cloneChatAttachment = (srcAttachmentId: number): Promise<ChatAttachment> =>
  api(`/api/chat/attachments/${srcAttachmentId}/clone`, { method: "POST" });

/** Загрузка с progress-callback'ом через XHR (fetch не даёт upload-прогресс).
 *  abortController.signal — отмена upload через abort(). */
/** Internal: single upload attempt. */
function uploadChatFileOnce(
  file: File,
  onProgress: (loaded: number, total: number) => void,
  signal?: AbortSignal,
): Promise<ChatAttachment> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
    xhr.open("POST", `${base}/chat/upload`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(e.loaded, e.total); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try { resolve(JSON.parse(xhr.responseText)); }
        catch (e) { reject(e); }
      } else {
        // Прокидываем статус отдельным полем — retry-обёртка решит ретраить или нет.
        const err = new Error(`HTTP ${xhr.status}: ${xhr.responseText}`);
        (err as Error & { httpStatus?: number }).httpStatus = xhr.status;
        reject(err);
      }
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    if (signal) {
      if (signal.aborted) { xhr.abort(); return; }
      signal.addEventListener("abort", () => xhr.abort());
    }
    const fd = new FormData();
    fd.append("file", file);
    xhr.send(fd);
  });
}

/** Public upload with retry on transient gateway errors (408/502/503/504/Network).
 *  3 попытки максимум с exponential backoff 1s/3s. Прерывание (Abort) НЕ ретраим.
 *  4xx (кроме 408) — НЕ ретраим (413 файл слишком большой, 401 не авторизован и т.п.) */
export const uploadChatFileWithProgress = async (
  file: File,
  onProgress: (loaded: number, total: number) => void,
  signal?: AbortSignal,
): Promise<ChatAttachment> => {
  const MAX_ATTEMPTS = 3;
  const RETRY_DELAYS = [1000, 3000];
  let lastErr: unknown = null;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      return await uploadChatFileOnce(file, onProgress, signal);
    } catch (err) {
      lastErr = err;
      // AbortError — пользователь сам отменил, не ретраим.
      if (err instanceof DOMException && err.name === "AbortError") throw err;
      if (signal?.aborted) throw err;
      const status = (err as Error & { httpStatus?: number }).httpStatus ?? 0;
      const isNetworkError = (err as Error)?.message === "Network error";
      const isTransient = isNetworkError || [408, 502, 503, 504].includes(status);
      if (!isTransient || attempt === MAX_ATTEMPTS) throw err;
      const delay = RETRY_DELAYS[attempt - 1] ?? 3000;
      await new Promise(r => setTimeout(r, delay));
    }
  }
  throw lastErr;
};
/** URL для скачивания/просмотра — учитывает VITE_API_BASE (path-prefix на prod). */
export const chatAttachmentUrl = (id: number): string => {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  return `${base}/chat/attachments/${id}/download`;
};
export const chatVideoThumbnailUrl = (id: number): string => {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  return `${base}/chat/attachments/${id}/thumbnail`;
};
/** 600px JPEG preview для картинок — для srcset на мобиле (small-screen variant).
 *  Если у оригинала ≤700px по большей стороне, backend отдаёт сам оригинал. */
export const chatAttachmentPreviewUrl = (id: number): string => {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  return `${base}/chat/attachments/${id}/preview`;
};
/** Универсальная JPEG превью-картинка для file-карточек (Яндекс.Диск-стиль):
 *  image/video → preview/thumbnail, PDF → 1-ая страница через pdftoppm,
 *  Office (docx/xlsx/pptx/...) → soffice → PDF → pdftoppm. Возвращает 415
 *  для типов без превью (zip/exe/...) — фронт скрывает картинку и
 *  показывает иконку-fallback. */
export const chatAttachmentPreviewImageUrl = (id: number): string => {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  return `${base}/chat/attachments/${id}/preview-image`;
};
export const pinChatMessage = (messageId: number): Promise<ChatMessage> =>
  api(`/api/chat/messages/${messageId}/pin`, { method: "POST" });
export const unpinChatMessage = (messageId: number): Promise<void> =>
  api(`/api/chat/messages/${messageId}/pin`, { method: "DELETE" });
export const fetchChatPinned = (topicId: number): Promise<ChatMessage[]> =>
  api(`/api/chat/topics/${topicId}/pinned`);
export const toggleChatReaction = (messageId: number, emoji: string): Promise<ChatReaction[]> =>
  api(`/api/chat/messages/${messageId}/reactions`, {
    method: "POST", body: JSON.stringify({ emoji }),
  });

// ВАЖНО: должен соответствовать ALLOWED_REACTIONS на backend (chat.py).
// При расхождении: юзер тапает эмодзи которой нет в backend whitelist →
// 400 "Неподдерживаемая реакция" → реакция не проставляется.
// Текущий набор ~50 шт — TG-standard: топ-8 quick + эмоции + жесты + командные.
export const REACTION_EMOJI = [
  // Топ-8 — стандартные TG quick reactions (в pill'е)
  "👍", "❤️", "🔥", "🥰", "👏", "😁", "🤔", "🤯",
  // Эмоции
  "😱", "🤬", "😢", "🎉", "🤩", "🙏", "👌", "🕊",
  // Прочие
  "🤡", "🥱", "🥴", "😍", "🐳", "❤️‍🔥", "🌚", "💯",
  // Развлекательные
  "🤣", "⚡", "🍌", "🏆", "💔", "🤨", "😐", "🍓",
  // Дополнительные (рабочие/командные)
  "👀", "✅", "❌", "⚠️", "💪", "🚀", "💡", "🎯",
  "🤝", "✨", "🌟", "💖", "😅", "😂", "😎", "⭐",
  "🥳", "😴",
] as const;

/* ── /api/production — производственный календарь ────────────── */
export interface ProductionProject {
  id: number;
  amo_lead_id: number | null;
  client_name: string;
  client_amo_url: string | null;
  period_label: string | null;
  /** Год срока «Период» (в period_label только месяцы). */
  plan_year?: number | null;
  /** Период посчитан по «Датам работ» (29.09.2026) — руками не правится. */
  period_auto?: boolean;
  status: string;                  // 'in_progress' | 'completed' | 'not_signed' | 'awaiting'
  work_dates: string | null;
  /** «Когда выполнен» — только год, если точной даты нет. */
  completed_year: number | null;
  /** «Когда выполнен» — точная дата в формате YYYY-MM-DD. Приоритетна для отображения. */
  completed_date: string | null;
  sum_contract: number | null;
  sum_expected: number | null;
  sum_actual: number | null;
  payment_type: string | null;     // 'cash' | 'non_cash'
  location: string | null;
  what_done: string | null;
  brick_color: string | null;
  brick_count: number | null;
  gazebo_size: string | null;
  accommodation: string | null;
  water_source: string | null;
  needs_pump: string | null;
  comment: string | null;
  is_manual: boolean;
  lead_created_at: string | null;
  in_planning: boolean;
  update_log: { ts: string; source: string; fields: Record<string, { old: unknown; new: unknown }> }[] | null;
  notes_log: { ts: string; author_id: string | null; author_name: string; text: string }[] | null;
  field_engineer_user_id: string | null;
  field_engineer_name: string | null;
  field_engineer_manual: boolean;
  field_engineer_suggested_user_id: string | null;
  field_engineer_suggested_name: string | null;
  /** Менеджер отдела продаж — из visits.manager_name по amo_lead_id */
  sales_manager_name?: string | null;
  /** Привязка к папке Диска (chat-топик kind='drive' + drive_folders.id) */
  drive_topic_id?: number | null;
  drive_folder_id?: number | null;
  /** Этап сопровождения проекта (внутри статуса «В работе»):
   *  materials | foundation | walls | roof | glazing | bbq | handover */
  support_stage: string | null;
  created_at: string;
  updated_at: string;
}
export interface ProductionList {
  items: ProductionProject[];
  kpi: {
    total_expected: number;
    total_actual: number;
    clients_count: number;
    avg_contract: number;
  };
}
export type ProductionPatch = Partial<Omit<ProductionProject, "id" | "amo_lead_id" | "is_manual" | "created_at" | "updated_at">>;
export const fetchProductionList = (filter: { period?: string; status?: string } = {}): Promise<ProductionList> => {
  const q = new URLSearchParams();
  if (filter.period) q.set("period", filter.period);
  if (filter.status) q.set("status", filter.status);
  const qs = q.toString();
  return api(`/api/production${qs ? "?" + qs : ""}`);
};

export const fetchProductionProject = (id: number): Promise<ProductionProject> =>
  api(`/api/production/${id}`);

export interface ProductionAmoLeadResponse {
  lead?: Record<string, unknown>;
  error?: string;
}
export const fetchProductionAmo = (id: number): Promise<ProductionAmoLeadResponse> =>
  api(`/api/production/${id}/amo`);

export interface ProductionCashflowExpense {
  id: number;
  date: string;
  amount: number;
  description: string | null;
  supplier: string | null;
  payment_method: string;
  category_name: string | null;
  category_kind: string | null;
  created_by_name: string | null;
  note: string | null;
}
export interface ProductionCashflowRevenue {
  id: number;
  received_at: string;
  amount: number;
  counterparty: string | null;
  type: string;
  payment_method: string;
  created_by_name: string | null;
  note: string | null;
}
export interface ProductionCashflowResponse {
  linked_object_id: number | null;
  expenses: ProductionCashflowExpense[];
  revenues: ProductionCashflowRevenue[];
  totals: {
    income_total: number;
    expense_total: number;
    balance: number;
    income_count: number;
    expense_count: number;
  };
}
export const fetchProductionCashflow = (id: number): Promise<ProductionCashflowResponse> =>
  api(`/api/production/${id}/cashflow`);
export const createProductionProject = (data: ProductionPatch): Promise<ProductionProject> =>
  api(`/api/production`, { method: "POST", body: JSON.stringify(data) });
export const updateProductionProject = (id: number, data: ProductionPatch): Promise<ProductionProject> =>
  api(`/api/production/${id}`, { method: "PATCH", body: JSON.stringify(data) });
/** Сроки стройки по доске Производства: взят в работу, сдан, срок; средние — по сданным (29.09.2026). */
export interface ProductionBuildTerm {
  production_project_id: number;
  start: string | null;
  done: string | null;
  build_days: number | null;
  contract_signed_at: string | null;
  contract_days: number | null;
  is_promotion: boolean;
}
export interface ProductionBuildTerms {
  items: ProductionBuildTerm[];
  done_n: number;
  avg_build_days: number | null;
  n_build: number;
  /** От договора до сдачи — без акционных договоров. */
  avg_contract_days: number | null;
  n_contract: number;
  board_since: string | null;
}
export const fetchProductionBuildTerms = (): Promise<ProductionBuildTerms> => api("/api/production/build-terms");
/** Можно ли поставить «Завершён»: приходы в «Деньгах» должны сойтись с договором (sumContract — из формы). */
export const fetchProductionCanComplete = (id: number, sumContract?: number | null): Promise<{ ok: boolean; reason: string | null }> =>
  api(`/api/production/${id}/can-complete${sumContract != null ? `?sum_contract=${sumContract}` : ""}`);
export const deleteProductionProject = (id: number): Promise<void> =>
  api(`/api/production/${id}`, { method: "DELETE" });
export const addProductionNote = (id: number, text: string): Promise<ProductionProject> =>
  api(`/api/production/${id}/notes`, { method: "POST", body: JSON.stringify({ text }) });
export const deleteProductionNote = (id: number, idx: number): Promise<ProductionProject> =>
  api(`/api/production/${id}/notes/${idx}`, { method: "DELETE" });
export interface SyncDiffField {
  old: unknown;
  new: unknown;
}
export interface SyncPendingDiff {
  pp_id: number;
  amo_lead_id: number;
  amo_url: string | null;
  current_name: string | null;
  fields: { client_name?: SyncDiffField; sum_contract?: SyncDiffField; sum_expected?: SyncDiffField };
}
export interface SyncProductionResult {
  created: number;
  skipped_existing: number;
  skipped_deleted: number;
  deduped: number;
  completed_marked?: number;
  total_deals_in_stage: number;
  pending_diffs?: SyncPendingDiff[];
}
export const syncProductionFromAmo = (): Promise<SyncProductionResult> =>
  api(`/api/production/sync`, { method: "POST" });

export interface SyncApplyItem {
  pp_id: number;
  fields: { name: "client_name" | "sum_contract" | "sum_expected"; new: string | number | null }[];
  propagate_to_po?: boolean;
}
export const applySyncDiffs = (items: SyncApplyItem[]): Promise<{ pp_updated: number; po_updated: number }> =>
  api(`/api/production/sync/apply`, { method: "POST", body: JSON.stringify({ items }) });

/* ── /api/visits — расписание выездов ───────────────────────── */
export interface VisitNote {
  id: number;
  text: string;
  author_name: string | null;
  created_at: string;
}
export interface Visit {
  id: number;
  amo_lead_id: number | null;
  /** Сделка CRM (07.10.2026): по ней щелчок по выезду открывает карточку. */
  crm_lead_id?: number | null;
  client_name: string;
  client_phone: string | null;
  client_amo_url: string | null;
  manager_name: string | null;
  engineer_name: string | null;
  address: string | null;
  region: string | null;
  visit_at: string | null;
  duration_min: number;
  visit_type: string;
  status: string;
  amo_stage: string | null;
  comment: string | null;
  is_manual: boolean;
  created_at: string;
  updated_at: string;
  notes?: VisitNote[];
  production_project_id?: number | null;
}
export interface VisitList {
  items: Visit[];
  kpi: {
    total: number;
    today: number;
    this_week: number;
    completed: number;
    no_date: number;
  };
}
export type VisitPatch = Partial<Omit<Visit, "id" | "is_manual" | "created_at" | "updated_at" | "client_name">> & {
  client_name?: string | null;
};
export const fetchVisitsList = (filter: { status?: string; manager?: string; region?: string; amo_stage?: string; amo_lead_id?: number; client_phone?: string; crm_lead_id?: number; exclude_post_contract?: boolean } = {}): Promise<VisitList> => {
  const q = new URLSearchParams();
  if (filter.status) q.set("status", filter.status);
  if (filter.manager) q.set("manager", filter.manager);
  if (filter.region) q.set("region", filter.region);
  if (filter.amo_stage) q.set("amo_stage", filter.amo_stage);
  if (filter.amo_lead_id) q.set("amo_lead_id", String(filter.amo_lead_id));
  if (filter.client_phone) q.set("client_phone", filter.client_phone);   // сделка без Amo (04.10.2026)
  if (filter.crm_lead_id) q.set("crm_lead_id", String(filter.crm_lead_id));
  if (filter.exclude_post_contract) q.set("exclude_post_contract", "true");
  const qs = q.toString();
  return api(`/api/visits${qs ? "?" + qs : ""}`);
};
export const createVisit = (data: VisitPatch): Promise<Visit> =>
  api(`/api/visits`, { method: "POST", body: JSON.stringify(data) });
export const updateVisit = (id: number, data: VisitPatch): Promise<Visit> =>
  api(`/api/visits/${id}`, { method: "PATCH", body: JSON.stringify(data) });
export const deleteVisit = (id: number): Promise<void> =>
  api(`/api/visits/${id}`, { method: "DELETE" });
export const syncVisitsFromAmo = (): Promise<{ created: number; updated: number; skipped_deleted: number; deduped: number; total_deals_in_visit_stages: number }> =>
  api(`/api/visits/sync`, { method: "POST" });
export const ensureVisitProductionCard = (visitId: number): Promise<{ project_id: number; created: boolean }> =>
  api(`/api/visits/${visitId}/ensure-production`, { method: "POST" });

/* ── /api/avatars — аватарки пользователей ─────────────────── */
export const uploadMyAvatar = (file: File): Promise<{ avatar_filename: string }> => {
  const fd = new FormData();
  fd.append("file", file);
  return api(`/api/avatars/me`, { method: "POST", body: fd });
};
export const deleteMyAvatar = (): Promise<void> =>
  api(`/api/avatars/me`, { method: "DELETE" });
/** URL аватара пользователя (учитывает VITE_API_BASE). filename как cache-buster.
 *  size=128 default — баланс retina-качества (до 64px CSS на 2x) и трафика
 *  (~5-8KB JPEG вместо 100KB-2MB оригинала, и в 3x меньше чем было size=256).
 *  Для drawer/header (80+px CSS) можно передать size=192 явно.
 *  Для lightbox — userAvatarUrlFull (без resize). */
export const userAvatarUrl = (userId: string, filename?: string | null, size: number = 128): string | null => {
  if (!filename) return null;
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  return `${base}/avatars/${userId}?v=${encodeURIComponent(filename)}&size=${size}`;
};
export const userAvatarUrlFull = (userId: string, filename?: string | null): string | null => {
  if (!filename) return null;
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  return `${base}/avatars/${userId}?v=${encodeURIComponent(filename)}`;
};

/** URL аватарки топика (с cache-busting через ?v=filename).
 *  size=128 default по тем же причинам что и user avatars. */
export const topicAvatarUrl = (topicId: number, filename?: string | null, size: number = 128): string | null => {
  if (!filename) return null;
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  return `${base}/chat/topic-avatars/${topicId}?v=${encodeURIComponent(filename)}&size=${size}`;
};
export const uploadTopicAvatar = async (topicId: number, file: File): Promise<{ avatar_filename: string }> => {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${base}/chat/topics/${topicId}/avatar`, {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Upload failed: ${res.status}`);
  return res.json();
};
export const deleteTopicAvatar = async (topicId: number): Promise<void> => {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  const res = await fetch(`${base}/chat/topics/${topicId}/avatar`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
};


/** ID system-юзера «Бот зарплаты». Жёстко сидится backend'ом в lifespan. */
export const BOT_SALARY_USER_ID = "bbbbbbbb-bbbb-4bbb-bbbb-bbbbbbbb5a1a";

/** Admin-only: обновить display_name бота. */
export const updateBotProfile = async (patch: { display_name?: string }): Promise<{ user_id: string; display_name: string | null; avatar_filename: string | null }> => {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  const res = await fetch(`${base}/chat/bot`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(patch),
  });
  if (!res.ok) throw new Error(`Bot update failed: ${res.status}`);
  return res.json();
};

/** Admin-only: загрузить аватар бота. */
export const uploadBotAvatar = async (file: File): Promise<{ avatar_filename: string }> => {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  const fd = new FormData();
  fd.append("file", file);
  const res = await fetch(`${base}/chat/bot/avatar`, {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Bot avatar upload failed: ${res.status}`);
  return res.json();
};

/** Admin-only: установить аватар бота из pack-иконки (1..100). */
export const setBotAvatarFromPack = async (index: number): Promise<{ avatar_filename: string; pack_index: number }> => {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  const res = await fetch(`${base}/chat/bot/avatar/pack`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ index }),
  });
  if (!res.ok) throw new Error(`Bot pack-avatar failed: ${res.status}`);
  return res.json();
};

/** Admin-only: удалить аватар бота. */
export const deleteBotAvatar = async (): Promise<void> => {
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  const res = await fetch(`${base}/chat/bot/avatar`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!res.ok) throw new Error(`Bot avatar delete failed: ${res.status}`);
};



/** Обновить свой профиль (display_name / position / bio). PATCH /api/users/me. */
export const updateMyProfile = (patch: {
  display_name?: string | null;
  position?: string | null;
  bio?: string | null;
  birthday?: string | null;
}): Promise<unknown> =>
  api(`/api/users/me`, { method: "PATCH", body: JSON.stringify(patch) });

/* ── /api/profiles — публичные карточки + heartbeat ──────────── */
export interface ProfileStats {
  photos: number;
  files: number;
  voice_messages: number;
  links: number;
}
export interface PublicUser {
  id: string;
  display_name: string | null;
  avatar_filename: string | null;
  role: string;
  is_superuser: boolean;
  position: string | null;
  bio: string | null;
  phone: string | null;
  username: string | null;
  departments: string[];
  last_seen_at: string | null;
  birthday: string | null;          // "MM-DD"
  stats: ProfileStats | null;
  /** Подтверждённые организации человека (03.10.2026). */
  orgs?: Array<{ id: number; name: string; is_home: boolean; role: "owner" | "admin" | "member" }>;
}
export const fetchUserPublic = (userId: string): Promise<PublicUser> =>
  api(`/api/profiles/${userId}`);

export interface UserMediaItem {
  id: number;
  attachment_id: number | null;
  message_id: number;
  kind: "photo" | "file" | "voice" | "link";
  title: string;
  subtitle: string | null;
  size_bytes: number | null;
  duration_sec: number | null;
  mime: string | null;
  created_at: string;
  topic_id: number;
  departments?: string[];
}
export const fetchUserMedia = (userId: string, kind: "photo" | "file" | "voice" | "link"): Promise<UserMediaItem[]> =>
  api(`/api/profiles/${userId}/media?kind=${kind}&limit=200`);

/* ── Typing-индикатор ────────────────────────────────────────── */
export interface TypingUser {
  user_id: string;
  display_name: string;
  avatar_filename: string | null;
}
export const postChatTyping = (topicId: number): Promise<void> =>
  api(`/api/chat/topics/${topicId}/typing`, { method: "POST" });
export const fetchChatTyping = (topicId: number): Promise<TypingUser[]> =>
  api(`/api/chat/topics/${topicId}/typing`);
/** Bulk: typing для всех доступных топиков. Возвращает {topic_id: TypingUser[]}. */
export const fetchAllTyping = (): Promise<Record<string, TypingUser[]>> =>
  api(`/api/chat/typing`);

/* ── Notifications (ответы на твои сообщения) ────────────────── */
export const markNotificationsSeen = (upToId: number): Promise<void> =>
  api(`/api/chat/notifications/mark-seen?up_to_id=${upToId}`, { method: "POST" });

/* ── Heartbeat / offline beacon ──────────────────────────────── */
export const heartbeat = (): Promise<void> =>
  api(`/api/profiles/heartbeat`, { method: "POST" });

/** sendBeacon — для pagehide: гарантированно доставляется на закрытии вкладки.
 *  Возвращает true если запрос поставлен в очередь браузером. */
export function sendOfflineBeacon(): boolean {
  if (typeof navigator === "undefined" || !navigator.sendBeacon) return false;
  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";
  // Пустое тело + same-origin POST с auto-куками. Браузер сам гарантирует доставку.
  return navigator.sendBeacon(`${base}/profiles/offline`);
}


/* ── /api/profiles/admin/access — админ-панель доступа к мессенджеру ─ */
export interface AccessRow {
  id: string;
  display_name: string | null;
  avatar_filename: string | null;
  role: string;
  is_superuser: boolean;
  departments: string[];
  can_access_messenger: boolean;
  push_subscriptions: number;
  push_muted: boolean;
  topic_count: number;
  last_seen_at: string | null;
  is_active?: boolean;
  email?: string | null;
  created_at?: string | null;
}
export interface AccessListOut { users: AccessRow[] }
export const fetchAdminAccessList = (): Promise<AccessListOut> => api("/api/profiles/admin/access");
export const setUserMessengerAccess = (userId: string, can: boolean): Promise<AccessRow> =>
  api(`/api/profiles/admin/access/${encodeURIComponent(userId)}`, {
    method: "PATCH", body: JSON.stringify({ can_access_messenger: can }),
  });

/* ── /api/profiles/meta/taxonomy — единый источник ролей+отделов ─ */
export interface TaxonomyEntry { key: string; label: string; default_color: string }
export interface ProfilesMeta { roles: TaxonomyEntry[]; departments: TaxonomyEntry[] }
export const fetchProfilesMeta = (): Promise<ProfilesMeta> => api("/api/profiles/meta/taxonomy");

/* ── /api/profiles/me/username — выбор @handle ─ */
export interface UsernameCheckOut { available: boolean; valid: boolean; reason?: string | null }
export const checkUsername = (name: string): Promise<UsernameCheckOut> =>
  api(`/api/profiles/me/username/check?name=${encodeURIComponent(name)}`);
export const setUsername = (username: string | null): Promise<UsernameCheckOut> =>
  api("/api/profiles/me/username", { method: "POST", body: JSON.stringify({ username }) });

/* ── /api/me/telegram-status, /api/bind-telegram, /api/unbind-telegram ─ */
export interface TgStatus { bound: boolean; telegram_id: number | null; telegram_username: string | null; }
export const fetchTelegramStatus = (): Promise<TgStatus> => api("/api/me/telegram-status");
export const bindTelegram = (code: string): Promise<{ telegram_id: number; telegram_username: string | null }> =>
  api("/api/bind-telegram", { method: "POST", body: JSON.stringify({ code }) });
export const unbindTelegram = (): Promise<{ status: string }> =>
  api("/api/unbind-telegram", { method: "POST" });


/* ── /api/admin/users — реальные fastapi-users (используются для login) ─ */
export interface AdminUser {
  id: string;
  email: string;
  display_name: string | null;
  avatar_filename: string | null;
  role: "admin" | "head_op" | "op_manager" | string;
  position: string | null;
  is_active: boolean;
  is_superuser: boolean;
  is_verified: boolean;
  notifications_disabled_at?: string | null;
  can_access_finance?: boolean;
  can_access_messenger?: boolean;
  can_access_settings?: boolean;
  can_use_calls_ai?: boolean;
  can_use_violations_ai?: boolean;
  can_use_assistant?: boolean;
  can_access_registry?: boolean;
  departments: string[];
  phone?: string | null;
  username?: string | null;
}
export const fetchAdminUsers = (params?: { pending_only?: boolean }): Promise<AdminUser[]> => {
  const q = params?.pending_only ? "?pending_only=true" : "";
  return api(`/api/admin/users/${q}`);
};
export const approveAdminUser = (userId: string): Promise<AdminUser> =>
  api(`/api/admin/users/${userId}/approve`, { method: "POST" });
export const deleteAdminUser = (userId: string): Promise<{ status: string }> =>
  api(`/api/admin/users/${userId}`, { method: "DELETE" });
/** PATCH к fastapi-users роутеру — менять role / is_active / display_name / position. */
export const patchAdminUser = (userId: string, payload: Partial<{ role: string; is_active: boolean; display_name: string; password: string; position: string | null; can_access_finance: boolean; can_access_messenger: boolean; can_access_settings: boolean; can_use_calls_ai: boolean; can_use_violations_ai: boolean; can_use_assistant: boolean; can_access_registry: boolean; departments: string[] }>): Promise<AdminUser> =>
  api(`/api/users/${userId}`, { method: "PATCH", body: JSON.stringify(payload) });

/* ── /api/profiles/by-position — список юзеров с заданной должностью ─ */
export interface UserShort {
  id: string;
  display_name: string | null;
  position: string | null;
  avatar_filename?: string | null;
  departments?: string[];
}
export const fetchUsersByPosition = (position: string): Promise<UserShort[]> =>
  api(`/api/profiles/by-position?position=${encodeURIComponent(position)}`);
export const fetchUsersByDepartment = (department: string): Promise<UserShort[]> =>
  api(`/api/profiles/by-department?department=${encodeURIComponent(department)}`);
export const fetchUsersByRole = (role: string): Promise<UserShort[]> =>
  api(`/api/profiles/by-role?role=${encodeURIComponent(role)}`);
/** Список активных юзеров — для @-mention autocomplete в чате. */
export interface TopicStats { members_count: number; online_count: number }
export const fetchTopicStats = (topicId: number): Promise<TopicStats> =>
  api(`/api/chat/topics/${topicId}/stats`);

export const fetchActiveUsers = (): Promise<UserShort[]> =>
  api(`/api/profiles/active`);

/* ── /api/chat — управление топиками + поиск ───────────────── */
export const createChatTopic = (data: {
  name: string;
  icon?: string | null;
  sort_order?: number;
  kind?: "public" | "private_group";
  member_ids?: string[];
  /** Организация чата (03.10.2026). */
  org_id?: number | null;
}): Promise<ChatTopic> =>
  api(`/api/chat/topics`, { method: "POST", body: JSON.stringify(data) });

/* ── /api/chat/dm + members CRUD ────────────────────────────── */
export const findDm = (partnerUserId: string): Promise<{ topic_id: number | null }> =>
  api(`/api/chat/dm/find/${encodeURIComponent(partnerUserId)}`);
export const getOrCreateDm = (partnerUserId: string): Promise<ChatTopic> =>
  api(`/api/chat/dm/with/${encodeURIComponent(partnerUserId)}`, { method: "POST" });

export const fetchTopicMembers = (topicId: number): Promise<ChatTopicMember[]> =>
  api(`/api/chat/topics/${topicId}/members`);

export const addTopicMembers = (topicId: number, userIds: string[]): Promise<ChatTopicMember[]> =>
  api(`/api/chat/topics/${topicId}/members`, { method: "POST", body: JSON.stringify({ user_ids: userIds }) });

export const removeTopicMember = (topicId: number, userId: string): Promise<void> =>
  api(`/api/chat/topics/${topicId}/members/${encodeURIComponent(userId)}`, { method: "DELETE" });

export const convertTopicKind = (topicId: number, payload: { kind: "public" | "private_group"; member_ids?: string[] }): Promise<ChatTopic> =>
  api(`/api/chat/topics/${topicId}/convert`, { method: "POST", body: JSON.stringify(payload) });
export const setTopicMemberRole = (
  topicId: number, userId: string, role: "member" | "admin"
): Promise<ChatTopicMember[]> =>
  api(`/api/chat/topics/${topicId}/members/${encodeURIComponent(userId)}/role`,
      { method: "PATCH", body: JSON.stringify({ role }) });

export const pinTopicForMe = (topicId: number): Promise<void> =>
  api(`/api/chat/topics/${topicId}/pin-for-me`, { method: "POST" });

export const unpinTopicForMe = (topicId: number): Promise<void> =>
  api(`/api/chat/topics/${topicId}/pin-for-me`, { method: "DELETE" });

export const pinTopicForAll = (topicId: number): Promise<void> =>
  api(`/api/chat/topics/${topicId}/pin-for-all`, { method: "POST" });

export const unpinTopicForAll = (topicId: number): Promise<void> =>
  api(`/api/chat/topics/${topicId}/pin-for-all`, { method: "DELETE" });

export const markTopicUnread = (topicId: number): Promise<void> =>
  api(`/api/chat/topics/${topicId}/mark-unread`, { method: "POST" });

export interface ChatFolder {
  id: number;
  name: string;
  icon: string | null;
  sort_order: number;
  topic_ids: number[];
}
export const fetchChatFolders = (): Promise<ChatFolder[]> =>
  api(`/api/chat/folders`);
export const createChatFolder = (payload: { name: string; icon?: string | null }): Promise<ChatFolder> =>
  api(`/api/chat/folders`, { method: "POST", body: JSON.stringify(payload) });
export const editChatFolder = (id: number, payload: { name: string; icon?: string | null }): Promise<ChatFolder> =>
  api(`/api/chat/folders/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
export const deleteChatFolder = (id: number): Promise<void> =>
  api(`/api/chat/folders/${id}`, { method: "DELETE" });
export const addTopicToFolder = (folderId: number, topicId: number): Promise<void> =>
  api(`/api/chat/folders/${folderId}/topics/${topicId}`, { method: "POST" });
export const removeTopicFromFolder = (folderId: number, topicId: number): Promise<void> =>
  api(`/api/chat/folders/${folderId}/topics/${topicId}`, { method: "DELETE" });

export const leaveTopic = (topicId: number): Promise<void> =>
  api(`/api/chat/topics/${topicId}/leave`, { method: "DELETE" });
export const editChatTopic = (id: number, data: { name?: string; icon?: string | null; sort_order?: number; department?: string | null }): Promise<ChatTopic> =>
  api(`/api/chat/topics/${id}`, { method: "PATCH", body: JSON.stringify(data) });
export const deleteChatTopic = (id: number): Promise<void> =>
  api(`/api/chat/topics/${id}`, { method: "DELETE" });
export const reorderChatTopics = (items: { id: number; sort_order: number }[]): Promise<void> =>
  api(`/api/chat/topics/reorder`, { method: "POST", body: JSON.stringify(items) });

/** Ручной порядок ЗАКРЕПЛЁННЫХ чатов текущего юзера (per-user). */
export const reorderChatPins = (topicIds: number[]): Promise<void> =>
  api(`/api/chat/topics/pins/reorder`, { method: "POST", body: JSON.stringify({ topic_ids: topicIds }) });

export interface ChatSearchHit {
  message_id: number;
  topic_id: number;
  topic_name: string;
  topic_icon: string | null;
  author_name: string;
  author_user_id?: string | null;
  author_avatar_filename?: string | null;
  snippet: string;
  created_at: string;
}
export const searchChatMessages = (q: string, topic_id?: number): Promise<ChatSearchHit[]> => {
  const params = new URLSearchParams({ q });
  if (topic_id !== undefined) params.set("topic_id", String(topic_id));
  return api(`/api/chat/search?${params.toString()}`);
};

export interface TopicMediaPhoto {
  attachment_id: number; message_id: number;
  filename: string; mime: string | null; size_bytes: number;
  created_at: string; author_name: string; author_user_id?: string | null;
}
export interface TopicMediaFile {
  attachment_id: number; message_id: number;
  filename: string; mime: string | null; size_bytes: number;
  created_at: string; author_name: string; author_user_id?: string | null;
}
export interface TopicMediaLink {
  message_id: number; url: string; text: string;
  created_at: string; author_name: string; author_user_id?: string | null;
}
export interface TopicMediaVoice {
  attachment_id: number; message_id: number;
  filename: string; mime: string | null; size_bytes: number;
  duration_sec: number | null; transcription: string | null; waveform: number[] | null;
  created_at: string; author_name: string; author_user_id?: string | null;
}
export interface TopicMedia {
  photos: TopicMediaPhoto[];
  files: TopicMediaFile[];
  voices: TopicMediaVoice[];
  links: TopicMediaLink[];
  /** Есть ли фото старше последнего в photos (листать photos_before) */
  has_more_photos?: boolean;
}
/** Медиа ОДНОГО топика. photosBefore — следующая страница фото (id вложения). */
export const fetchTopicMedia = (topicId: number, photosBefore?: number): Promise<TopicMedia> =>
  api(`/api/chat/topics/${topicId}/media${photosBefore ? `?photos_before=${photosBefore}` : ""}`);

/* ── Departments admin (taxonomy_departments) ──────────────────────── */
export interface DepartmentTaxonomy {
  key: string;
  label: string;
  default_color: string;
  sort_order: number;
  created_at?: string | null;
}
export const fetchDepartments = (): Promise<DepartmentTaxonomy[]> =>
  api(`/api/admin/departments/`);
export const createDepartment = (
  body: { key: string; label: string; default_color?: string; sort_order?: number },
): Promise<DepartmentTaxonomy> =>
  api(`/api/admin/departments/`, { method: "POST", body: JSON.stringify(body) });
export const patchDepartment = (
  key: string,
  body: Partial<{ label: string; default_color: string; sort_order: number }>,
): Promise<DepartmentTaxonomy> =>
  api(`/api/admin/departments/${encodeURIComponent(key)}`, { method: "PATCH", body: JSON.stringify(body) });
export const deleteDepartment = (key: string): Promise<void> =>
  api(`/api/admin/departments/${encodeURIComponent(key)}`, { method: "DELETE" });

/** /api/profiles/meta/taxonomy — единый источник правды для UI (роли + отделы из БД). */
export interface TaxonomyMeta {
  roles: { key: string; label: string; default_color: string }[];
  departments: { key: string; label: string; default_color: string }[];
}
export const fetchTaxonomy = (): Promise<TaxonomyMeta> =>
  api(`/api/profiles/meta/taxonomy`);


/** Должности, поддерживаемые в системе. */
export const POSITION_FIELD_ENGINEER = "Выездной инженер";
export const ROLE_FIELD_ENGINEER = "field_engineer";
export const POSITION_ASSISTANT = "Ассистент";
export const POSITION_ADMINISTRATOR = "Администратор";
export const POSITION_WAREHOUSE_KEEPER = "Кладовщик";
export const POSITION_ACCOUNTANT = "Бухгалтер";
export const POSITION_OPTIONS: string[] = [
  POSITION_FIELD_ENGINEER,
  POSITION_ASSISTANT,
  POSITION_ADMINISTRATOR,
  POSITION_WAREHOUSE_KEEPER,
  POSITION_ACCOUNTANT,
];

/* ── /api/push — Web Push подписки ──────────────────────────── */
export const fetchVapidPublicKey = (): Promise<{ key: string }> =>
  api(`/api/push/vapid-public-key`);
export const subscribePush = (sub: PushSubscriptionJSON & { user_agent?: string }): Promise<{ status: string }> =>
  api(`/api/push/subscribe`, { method: "POST", body: JSON.stringify(sub) });
export const unsubscribePush = (endpoint: string): Promise<void> =>
  api(`/api/push/subscribe`, { method: "DELETE", body: JSON.stringify({ endpoint }) });
export const sendTestPush = (): Promise<{ delivered: number; status: string }> =>
  api(`/api/push/test`, { method: "POST" });
export const fetchPushStatus = (): Promise<{ subscription_count: number; muted: boolean; muted_at: string | null }> =>
  api(`/api/push/status`);
export const muteNotifications = (): Promise<{ muted: boolean; muted_at: string }> =>
  api(`/api/push/mute`, { method: "POST" });
export const unmuteNotifications = (): Promise<{ muted: boolean }> =>
  api(`/api/push/unmute`, { method: "POST" });

/* ── Маркетинг — Я.Директ ─────────────────────────────────────── */
export interface MarketingAccountStatus {
  connected: boolean;
  login?: string | null;
  title?: string | null;
  connected_at?: string | null;
  last_sync_at?: string | null;
  last_sync_error?: string | null;
  token_expires_at?: string | null;
}
export interface MarketingOAuthStatus {
  configured: boolean;
  authorize_url?: string | null;
  error?: string | null;
}
export type MarketingZone = "green" | "yellow" | "red" | "nodata";
export interface MarketingTotals {
  impressions: number; clicks: number; cost_rub: number; conversions: number;
  ctr: number; avg_cpc_rub: number; cr: number; cpa_rub: number;
  sessions?: number; bounce_rate?: number; avg_pageviews?: number;
}
export interface MarketingOverview {
  period: { from: string; to: string };
  prev_period?: { from: string; to: string };
  totals: MarketingTotals;
  prev_totals?: MarketingTotals;
  zones?: { ctr: MarketingZone; cr: MarketingZone; cpc: MarketingZone; cpa: MarketingZone };
  deltas?: Record<string, number | null>;
  goals?: { goal_id: number; name: string; conversions: number; cpa_rub: number; cr: number; delta?: number | null }[];
  daily: { date: string; impressions: number; clicks: number; cost_rub: number; conversions: number; ctr: number; cpa_rub: number }[];
}
export interface MarketingCampaignRow {
  direct_id: number;
  name: string;
  type: string | null;
  state: string | null;
  status: string | null;
  daily_budget_rub: number | null;
  impressions: number;
  clicks: number;
  cost_rub: number;
  conversions: number;
  ctr: number;
  avg_cpc_rub: number;
  cr: number;
  cpa_rub: number;
  sessions?: number;
  bounce_rate?: number;
  avg_pageviews?: number;
  conv_by_goal?: Record<string, number>;
  zones?: { ctr: MarketingZone; cr: MarketingZone; cpc: MarketingZone; cpa: MarketingZone };
}
export const fetchMarketingStatus = (): Promise<MarketingAccountStatus> =>
  api("/api/marketing/status");
export const fetchMarketingOAuthStatus = (): Promise<MarketingOAuthStatus> =>
  api("/api/marketing/oauth-status");
// --- Кабинеты (мультиаккаунт, Этап 0d) ---
export interface MarketingAccount {
  id: number;
  login: string;
  title: string;
  connected_at?: string | null;
  last_sync_at?: string | null;
  last_sync_error?: string | null;
  token_expires_at?: string | null;
  metrika_counter_id?: number | null;
  attribution_model?: string | null;
  goal_ids: number[];
}
export const fetchMarketingAccounts = (): Promise<MarketingAccount[]> =>
  api("/api/marketing/accounts");
export const connectMarketing = (payload: { verification_code: string; login: string; title?: string; account_id?: number }): Promise<MarketingAccount> =>
  api("/api/marketing/oauth/connect", { method: "POST", body: JSON.stringify(payload) });
export const deleteMarketingAccount = (id: number): Promise<{ status: string }> =>
  api(`/api/marketing/accounts/${id}`, { method: "DELETE" });
export const updateMarketingAccount = (id: number, patch: { title?: string; login?: string }): Promise<MarketingAccount> =>
  api(`/api/marketing/accounts/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
export const disconnectMarketing = (): Promise<{ status: string }> =>
  api("/api/marketing/disconnect", { method: "POST" });
export const syncMarketing = (opts?: { days?: number; date_from?: string; date_to?: string; accountId?: number }): Promise<{ campaigns_synced: number; daily_rows_upserted: number; date_from: string; date_to: string; error?: string | null }> => {
  const q = new URLSearchParams();
  if (opts?.date_from && opts?.date_to) { q.set("date_from", opts.date_from); q.set("date_to", opts.date_to); }
  else q.set("days", String(opts?.days ?? 30));
  if (opts?.accountId) q.set("account_id", String(opts.accountId));
  return api(`/api/marketing/sync?${q.toString()}`, { method: "POST" });
};
export const fetchMarketingOverview = (params?: { date_from?: string; date_to?: string; account_id?: number }): Promise<MarketingOverview> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to) q.set("date_to", params.date_to);
  if (params?.account_id) q.set("account_id", String(params.account_id));
  const qs = q.toString();
  return api(`/api/marketing/overview${qs ? "?" + qs : ""}`);
};
export interface CampaignsResponse { campaigns: MarketingCampaignRow[]; goals: { goal_id: number; name: string }[] }
export const fetchMarketingCampaigns = (params?: { date_from?: string; date_to?: string; account_id?: number }): Promise<CampaignsResponse> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to) q.set("date_to", params.date_to);
  if (params?.account_id) q.set("account_id", String(params.account_id));
  const qs = q.toString();
  return api(`/api/marketing/campaigns${qs ? "?" + qs : ""}`);
};

// --- Цели Я.Метрики + модель атрибуции (Этап 0b) ---
export interface MetrikaCounter { counter_id: number; name: string; site: string; status: string }
export interface MarketingGoal { goal_id: number; name: string; type: string | null; is_retargeting: boolean }
export interface MarketingGoalsState {
  counter_id: number | null;
  attribution_model: string;
  attribution_options: string[];
  available: MarketingGoal[];
  selected: number[];
}
const acc1 = (accountId?: number) => (accountId ? `?account_id=${accountId}` : "");
const acc2 = (accountId?: number) => (accountId ? `&account_id=${accountId}` : "");
export const fetchMarketingMetrikaCounters = (accountId?: number): Promise<MetrikaCounter[]> =>
  api(`/api/marketing/metrika/counters${acc1(accountId)}`);
export const importMarketingGoals = (counterId: number, accountId?: number): Promise<{ counter_id: number; imported: number }> =>
  api(`/api/marketing/metrika/import-goals?counter_id=${counterId}${acc2(accountId)}`, { method: "POST" });
export const fetchMarketingGoals = (accountId?: number): Promise<MarketingGoalsState> =>
  api(`/api/marketing/goals${acc1(accountId)}`);
export const saveMarketingGoals = (payload: { goal_ids: number[]; attribution_model: string }, accountId?: number): Promise<{ goal_ids: number[]; attribution_model: string }> =>
  api(`/api/marketing/goals${acc1(accountId)}`, { method: "POST", body: JSON.stringify(payload) });

// --- Срезы устройства/гео/пол-возраст (Этап 1B) ---
export interface MarketingBreakdownRow {
  key: string; label: string;
  impressions: number; clicks: number; cost_rub: number; conversions: number;
  ctr: number; avg_cpc_rub: number; cr: number; cpa_rub: number;
  zones?: { ctr: MarketingZone; cr: MarketingZone; cpc: MarketingZone; cpa: MarketingZone };
}
export interface MarketingBreakdown { dimension: string; rows: MarketingBreakdownRow[]; error?: string }
export const fetchMarketingBreakdown = (dim: string, params?: { date_from?: string; date_to?: string }): Promise<MarketingBreakdown> => {
  const q = new URLSearchParams({ dim });
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to) q.set("date_to", params.date_to);
  return api(`/api/marketing/breakdown?${q.toString()}`);
};

export interface MarketingBreakdownInsights {
  summary?: string; observations?: string[]; recommendations?: string[];
  dimension?: string; generated_at?: string; model?: string; cached?: boolean; error?: string;
}
export const fetchBreakdownInsights = (dim: string, params: { date_from: string; date_to: string }, force = false): Promise<MarketingBreakdownInsights> => {
  const q = new URLSearchParams({ dim, date_from: params.date_from, date_to: params.date_to });
  if (force) q.set("force", "true");
  return api(`/api/marketing/breakdown/insights?${q.toString()}`);
};

// --- AI-инсайты (Этап 5) ---
export interface MarketingInsightVerdict { campaign: string; verdict: string; reason: string }
export interface MarketingInsights {
  summary?: string; observations?: string[]; verdicts?: MarketingInsightVerdict[];
  generated_at?: string; model?: string; cached?: boolean; error?: string; raw?: string;
  id?: number; trigger?: string; created_at?: string;
}
export const fetchMarketingInsights = (params?: { date_from?: string; date_to?: string; force?: boolean }): Promise<MarketingInsights> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to) q.set("date_to", params.date_to);
  if (params?.force) q.set("force", "true");
  const qs = q.toString();
  return api(`/api/marketing/insights${qs ? "?" + qs : ""}`);
};
export interface InsightVersion { id: number; created_at: string; trigger: string; model?: string; period: { from: string; to: string }; summary: string }
export const fetchInsightsHistory = (): Promise<{ versions: InsightVersion[] }> => api("/api/marketing/insights/history");
export const fetchInsightVersion = (id: number): Promise<MarketingInsights> => api(`/api/marketing/insights/${id}`);

// --- CRM-подключения (AmoCRM, Roistat, …) ---
export interface CrmType { type: string; label: string; wired: boolean }
export interface CrmConnection { id: string; type: string; name: string; domain: string; api_key: string; note: string }
export interface AmocrmStatus { connected: boolean; domain?: string | null; pipelines?: number; error?: string }
export const fetchCrmTypes = (): Promise<{ types: CrmType[] }> => api("/api/marketing/crm/types");
export const fetchCrmStatus = (): Promise<{ amocrm: AmocrmStatus }> => api("/api/marketing/crm/status");
export const fetchCrmConnections = (): Promise<{ connections: CrmConnection[] }> => api("/api/marketing/crm-connections");
export type CardOp = "metric" | "ratio" | "divide" | "multiply" | "sum" | "diff" | "scale";
export interface CustomCard { id: string; title: string; type: CardOp; a: string; b?: string; k?: number; format: "int" | "num2" | "rub" | "pct" }
export interface ChartSeries { key: string; color?: string; axis?: "L" | "R"; as?: "line" | "bar" }
export interface CustomChart {
  id: string; title: string;
  type?: "line" | "area" | "bar" | "combo";
  series?: ChartSeries[];
  // back-compat (одиночная метрика):
  metric?: string; kind?: "line" | "bar";
}
export interface CardsConfig { hidden: string[]; custom: CustomCard[]; charts_hidden: string[]; charts_custom: CustomChart[] }
export const fetchCardsConfig = (): Promise<CardsConfig> => api("/api/marketing/cards-config");
export const saveCardsConfig = (c: CardsConfig): Promise<CardsConfig> =>
  api("/api/marketing/cards-config", { method: "POST", body: JSON.stringify(c) });
export interface AmoOverride { mode: string; domain: string; token: string }
export const fetchAmoOverride = (): Promise<AmoOverride> => api("/api/marketing/crm/amo-override");
export const saveAmoOverride = (o: { mode: string; domain: string; token: string }): Promise<AmoOverride> =>
  api("/api/marketing/crm/amo-override", { method: "POST", body: JSON.stringify(o) });
export const saveCrmConnections = (connections: CrmConnection[]): Promise<{ connections: CrmConnection[] }> =>
  api("/api/marketing/crm-connections", { method: "POST", body: JSON.stringify({ connections }) });

// --- Рекламные источники (VK/TG/Авито/Google и др.) ---
export interface AdSource { source: string; label: string; wired: boolean }
export interface AdConnection { id: string; source: string; name: string; account?: string; api_key: string; note: string }
export const fetchAdSources = (): Promise<{ sources: AdSource[] }> => api("/api/marketing/ad-sources");
export const fetchAdConnections = (): Promise<{ connections: AdConnection[] }> => api("/api/marketing/ad-connections");
export const saveAdConnections = (connections: AdConnection[]): Promise<{ connections: AdConnection[] }> =>
  api("/api/marketing/ad-connections", { method: "POST", body: JSON.stringify({ connections }) });

// --- Связка с AmoCRM: конфигурация воронки (Этап 3) ---
export interface AmoCustomField { id: number; name: string; code: string | null; type: string | null }
export interface AmoStage { stage_id: number; name: string; pipeline_id: number; pipeline_name: string; sort: number; color: string | null }
export interface UtmRule { field_id: number | null; field_name: string | null; values: string[] }
export interface CrmSegment { name: string; match?: string; utm_rules: UtmRule[]; stage_ids: number[] }
export interface MarketingCrmConfig { segments: CrmSegment[] }
export const fetchAmoCustomFields = (): Promise<{ fields: AmoCustomField[]; error?: string }> =>
  api("/api/marketing/amo/custom-fields");
export const fetchAmoStages = (): Promise<{ stages: AmoStage[]; error?: string }> =>
  api("/api/marketing/amo/stages");
export interface AmoFieldValue { value: string; count: number }
export const fetchAmoFieldValues = (fieldId: number): Promise<{ values: AmoFieldValue[]; error?: string }> =>
  api(`/api/marketing/amo/field-values?field_id=${fieldId}`);
export const fetchCrmConfig = (): Promise<MarketingCrmConfig> =>
  api("/api/marketing/crm-config");
export const saveCrmConfig = (cfg: MarketingCrmConfig): Promise<MarketingCrmConfig> =>
  api("/api/marketing/crm-config", { method: "POST", body: JSON.stringify(cfg) });

// --- Воронка по сегментам (Этап 3, расчёт) ---
export interface FunnelStep { stage_id: number; name: string; color: string | null; count: number; conv_from_prev: number | null; cost_per_rub: number | null }
export interface FunnelSegment { name: string; match: string; total: number; won: number; cpo_rub: number | null; conv_total_won: number | null; steps: FunnelStep[] }
export interface MarketingFunnel { configured: boolean; period?: { from: string; to: string }; spend_rub?: number; segments: FunnelSegment[]; error?: string }
export const fetchMarketingFunnel = (params?: { date_from?: string; date_to?: string }): Promise<MarketingFunnel> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to) q.set("date_to", params.date_to);
  const qs = q.toString();
  return api(`/api/marketing/funnel${qs ? "?" + qs : ""}`);
};

export interface UnattributedDeal {
  id: number;
  name: string;
  price: number;
  created_at?: number | null;
  contract_at?: number | null;
  status_name?: string;
  pipeline_name?: string;
  utm_campaign?: string;
  manager?: string;
  amo_url?: string | null;
}
export interface CampaignFunnel {
  configured: boolean;
  stages: { stage_id: number; name: string }[];
  campaigns: Record<string, { total: number; stages: Record<string, number>; calls?: number; revenue?: number }>;
  /** Сделки, которые не привязались к кампании (utm_campaign пуст или не ID
   *  кампании). В таблице их не видно ни в одной строке — блок отдаётся
   *  отдельно, иначе «итого» молча меньше реальной воронки.
   *  Источник: services/marketing_crm.py. */
  unattributed?: {
    total: number;
    stages: Record<string, number>;
    empty: number;
    foreign: number;
    values: Record<string, number>;
    /** Сумма договоров без метки. */
    revenue?: number;
    /** Договоры без метки поимённо — иначе их нигде не видно. */
    deals?: UnattributedDeal[];
  };
  has_calls?: boolean;
  calls_mode?: string;
  error?: string;
}
export const fetchCallsMode = (): Promise<{ mode: string }> => api("/api/marketing/crm/calls-mode");
export const saveCallsMode = (mode: string): Promise<{ mode: string }> =>
  api("/api/marketing/crm/calls-mode", { method: "POST", body: JSON.stringify({ mode }) });

export interface MarketingTargets { goals: Record<string, number>; metrics: Record<string, number> }
export interface CommercialRow {
  period: string; ad_spend: number; marketing_exp: number; sales_exp: number; payroll: number; other_exp: number; unallocated: number;
  contracts: number; contracts_sum: number; sqm: number; done_sum: number; done_count: number; revenue: number; leads: number; impressions: number; clicks: number;
  marketing_total: number; sales_total: number; total_cost: number;
  cost_per_contract: number; cpc_marketing: number; cpc_sales: number; cpc_sales_other: number;
  ctr: number; avg_cpc: number; cr: number; cost_per_conv: number;
  payback: number; romi: number; roi: number; marketing_share: number; sales_share: number;
  gross_profit: number; gross_margin: number; op_profit: number; op_margin: number;
  rev_share_marketing: number; rev_share_sales: number;
  conv_by_goal?: Record<string, number>;
  funnel?: Record<string, number>;
}
export interface CommercialGoal { goal_id: number; name: string }
export interface CommercialFunnelStage { stage_id: number; name: string }
export interface CommercialBreakdownRow { name: string; contracts: number; contracts_sum: number; revenue: number; avg_check: number }
export interface CommercialPlan { contracts?: number | null; revenue?: number | null; max_cost_per_contract?: number | null; min_payback?: number | null }
export interface CommercialReport {
  period: { from: string; to: string }; weeks: CommercialRow[]; months: CommercialRow[]; total: CommercialRow;
  by_manager?: CommercialBreakdownRow[]; by_region?: CommercialBreakdownRow[]; plan?: CommercialPlan; goals?: CommercialGoal[]; funnel_stages?: CommercialFunnelStage[];
}
export interface CommercialExpenseGroup { category: string; amount: number; count: number }
export interface CommercialExpenseItem { date: string; category: string; amount: number; description: string; supplier: string }
export interface CommercialExpenseDetail { component: string; label: string; total: number; by_category: CommercialExpenseGroup[]; items: CommercialExpenseItem[] }
export const fetchCommercialExpenseDetail = (component: string, params?: { date_from?: string; date_to?: string }): Promise<CommercialExpenseDetail> => {
  const q = new URLSearchParams({ component });
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to) q.set("date_to", params.date_to);
  return api(`/api/marketing/commercial/expense-detail?${q.toString()}`);
};

export interface CommercialContract { id: number; client: string; contract_sum: number; signed: string; manager: string; region: string; revenue: number; excluded: boolean; amo_url?: string | null; lead_created?: string | null; won_date?: string | null }
export interface CommercialContractsList { contracts: CommercialContract[]; total: number; total_sum: number }
export const fetchCommercialContracts = (params?: { date_from?: string; date_to?: string }): Promise<CommercialContractsList> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to) q.set("date_to", params.date_to);
  const qs = q.toString();
  return api(`/api/marketing/commercial/contracts${qs ? "?" + qs : ""}`);
};
export const toggleCommercialContract = (id: number, excluded: boolean): Promise<{ excluded: number[] }> =>
  api("/api/marketing/commercial/contracts/exclude", { method: "POST", body: JSON.stringify({ id, excluded }) });

export const fetchCommercialPlan = (): Promise<CommercialPlan> => api("/api/marketing/commercial/plan");
export const saveCommercialPlan = (plan: CommercialPlan): Promise<CommercialPlan> =>
  api("/api/marketing/commercial/plan", { method: "POST", body: JSON.stringify(plan) });
export const fetchCommercialReport = (params?: { date_from?: string; date_to?: string }): Promise<CommercialReport> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to) q.set("date_to", params.date_to);
  const qs = q.toString();
  return api(`/api/marketing/commercial/report${qs ? "?" + qs : ""}`);
};

export const fetchTargets = (source: string, account_id?: number | null): Promise<MarketingTargets> => {
  const q = new URLSearchParams({ source });
  if (account_id) q.set("account_id", String(account_id));
  return api(`/api/marketing/targets?${q.toString()}`);
};
export const saveTargets = (source: string, account_id: number | null, goals: Record<string, number>, metrics: Record<string, number>): Promise<MarketingTargets> =>
  api("/api/marketing/targets", { method: "POST", body: JSON.stringify({ source, account_id, goals, metrics }) });
export interface FunnelDeal { id: number; name: string; price: number; created_at: number; status_name: string; utm_source: string | null; amo_url: string | null; excluded?: boolean }
export const fetchFunnelDeals = (stageId: number, params?: { date_from?: string; date_to?: string }): Promise<{ stage_id: number; stage_name?: string; deals: FunnelDeal[]; error?: string }> => {
  const q = new URLSearchParams({ stage_id: String(stageId) });
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to) q.set("date_to", params.date_to);
  return api(`/api/marketing/funnel/deals?${q.toString()}`);
};
export const setExcludedLead = (lead_id: number, excluded: boolean): Promise<{ excluded: number[] }> =>
  api("/api/marketing/excluded-leads", { method: "POST", body: JSON.stringify({ lead_id, excluded }) });
export const fetchSuccessStages = (): Promise<{ stage_ids: number[] }> => api("/api/marketing/success-stages");
export const saveSuccessStages = (stage_ids: number[]): Promise<{ stage_ids: number[] }> =>
  api("/api/marketing/success-stages", { method: "POST", body: JSON.stringify({ stage_ids }) });
export interface SuccessGroup { stage_id: number; stage_name: string; deals: FunnelDeal[] }
export const fetchSuccessDeals = (params?: { date_from?: string; date_to?: string }): Promise<{ groups: SuccessGroup[]; configured: boolean; total?: number; error?: string }> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to) q.set("date_to", params.date_to);
  const qs = q.toString();
  return api(`/api/marketing/success-deals${qs ? "?" + qs : ""}`);
};
export const fetchMarketingCampaignFunnel = (params?: { date_from?: string; date_to?: string }): Promise<CampaignFunnel> => {
  const q = new URLSearchParams();
  if (params?.date_from) q.set("date_from", params.date_from);
  if (params?.date_to) q.set("date_to", params.date_to);
  const qs = q.toString();
  return api(`/api/marketing/campaign-funnel${qs ? "?" + qs : ""}`);
};

/* ── /api/settings/vpn/ ──────────────────────────────────────── */
export interface VpnStatus {
  active: string;
  anthropic_http: number;
  egress_ok: boolean;
  almaty_tunnel: number;
}
export type VpnProfile = "almaty" | "nl1" | "nl2";

export const fetchVpnStatus = (): Promise<VpnStatus> =>
  api("/api/settings/vpn/status");

export const switchVpnProfile = (profile: VpnProfile): Promise<VpnStatus> =>
  api("/api/settings/vpn/switch", { method: "POST", body: JSON.stringify({ profile }) });

// ── Scheduled messages ───────────────────────────────────────────────
export interface ScheduledMessage {
  id: number;
  topic_id: number;
  topic_name: string | null;
  author_user_id: string;
  text: string;
  reply_to_id: number | null;
  attachment_ids: number[];
  scheduled_for: string;   // ISO
  created_at: string;
  sent_at: string | null;
  sent_message_id: number | null;
  cancelled_at: string | null;
  error: string | null;
}

export const createScheduledMessage = (topicId: number, payload: {
  text: string;
  reply_to_id?: number | null;
  attachment_ids?: number[];
  scheduled_for: string;   // ISO без tz (backend трактует как UTC)
}): Promise<ScheduledMessage> =>
  api(`/api/chat/topics/${topicId}/scheduled`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      text: payload.text,
      reply_to_id: payload.reply_to_id ?? null,
      attachment_ids: payload.attachment_ids ?? [],
      scheduled_for: payload.scheduled_for,
    }),
  });

export const fetchScheduledInTopic = (topicId: number): Promise<ScheduledMessage[]> =>
  api(`/api/chat/topics/${topicId}/scheduled`);

export const fetchAllMyScheduled = (): Promise<ScheduledMessage[]> =>
  api(`/api/chat/scheduled/all`);

export const patchScheduledMessage = (sid: number, payload: {
  text?: string;
  scheduled_for?: string;
}): Promise<ScheduledMessage> =>
  api(`/api/chat/scheduled/${sid}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

export const cancelScheduledMessage = (sid: number): Promise<void> =>
  api(`/api/chat/scheduled/${sid}`, { method: "DELETE" });

export const sendScheduledNow = (sid: number): Promise<ScheduledMessage> =>
  api(`/api/chat/scheduled/${sid}/send-now`, { method: "POST" });
